/**
 * Coverage derivations (ADR-0101) — every pure computation behind the
 * Coverage tab, kept out of the page component so it unit-tests without
 * React. The wire is a flat array of enriched Coverage rows; everything the
 * page renders (map fills, detail card, gaps strip, chip set) derives from
 * that array plus the live chip selection.
 *
 * Depth semantics: a state's depth for ONE case type is its count of ACTIVE
 * rows, capped at 3 ("3+"). Across a multi-type selection the state shades by
 * its WEAKEST selected type — `min` of the per-type depths — so the map
 * answers "where am I covered for everything I've selected", not "where does
 * anything exist". Paused rows (row-level or firm-level) never count toward
 * depth but still ride the wire so the detail card can show them muted.
 */

import { US_STATE_CODES, type CoverageRow } from "@command-center/shared";

export type Depth = 0 | 1 | 2 | 3;

/** Active = counts toward depth: row not paused AND firm not paused. */
export function isActiveRow(row: CoverageRow): boolean {
  return !row.rowPaused && row.firmRoutingStatus !== "Paused";
}

/** Active-row count for one state × case type, capped at 3 ("3+"). */
export function depthForType(
  rows: CoverageRow[],
  state: string,
  caseType: string,
): Depth {
  let count = 0;
  for (const r of rows) {
    if (r.state === state && r.caseType === caseType && isActiveRow(r)) {
      count++;
      if (count >= 3) return 3;
    }
  }
  return count as Depth;
}

/** Weakest-link depth for one state across the selected types. Empty
 *  selection → 0 (an unshaded map is honest when nothing is selected). */
export function depthForState(
  rows: CoverageRow[],
  state: string,
  selectedTypes: string[],
): Depth {
  if (selectedTypes.length === 0) return 0;
  let min: Depth = 3;
  for (const t of selectedTypes) {
    const d = depthForType(rows, state, t);
    if (d < min) min = d;
    if (min === 0) return 0;
  }
  return min;
}

/** Depth for every jurisdiction (50 states + DC) — drives the map fills and
 *  the small-state strip squares off one computation. */
export function depthByState(
  rows: CoverageRow[],
  selectedTypes: string[],
): Record<string, Depth> {
  const out: Record<string, Depth> = {};
  for (const code of US_STATE_CODES) {
    out[code] = depthForState(rows, code, selectedTypes);
  }
  return out;
}

export type DetailRow = {
  id: string;
  firmName: string;
  priority: number | null;
  feeHfPct: number | null;
  /** Muted in the card; excluded from depth. */
  paused: boolean;
};

export type StateDetail = {
  caseType: string;
  rows: DetailRow[];
};

/** Detail-card payload for one state: per selected type, rows sorted by
 *  priority asc (nulls last, then firm name). Selection order is preserved
 *  so the card reads in the same order as the chips. */
export function detailForState(
  rows: CoverageRow[],
  state: string,
  selectedTypes: string[],
): StateDetail[] {
  return selectedTypes.map((caseType) => ({
    caseType,
    rows: rows
      .filter((r) => r.state === state && r.caseType === caseType)
      .map((r) => ({
        id: r.id,
        firmName: r.firmName,
        priority: r.priority,
        feeHfPct: r.feeHfPct,
        paused: !isActiveRow(r),
      }))
      .sort(compareDetailRows),
  }));
}

function compareDetailRows(a: DetailRow, b: DetailRow): number {
  if (a.priority !== null && b.priority !== null && a.priority !== b.priority) {
    return a.priority - b.priority;
  }
  if (a.priority === null && b.priority !== null) return 1;
  if (a.priority !== null && b.priority === null) return -1;
  return a.firmName.localeCompare(b.firmName, undefined, {
    sensitivity: "base",
  });
}

export type CoverageGaps = {
  /** Jurisdictions at weakest-link depth 0 for the selection. */
  noCoverage: string[];
  /** Jurisdictions at weakest-link depth exactly 1. */
  oneDeep: string[];
  /** Active rows of the selected types missing a fee % — the hygiene chase. */
  missingFeeCount: number;
};

export function computeGaps(
  rows: CoverageRow[],
  selectedTypes: string[],
): CoverageGaps {
  const depths = depthByState(rows, selectedTypes);
  const noCoverage: string[] = [];
  const oneDeep: string[] = [];
  for (const code of US_STATE_CODES) {
    if (depths[code] === 0) noCoverage.push(code);
    else if (depths[code] === 1) oneDeep.push(code);
  }
  const selected = new Set(selectedTypes);
  let missingFeeCount = 0;
  for (const r of rows) {
    if (selected.has(r.caseType) && isActiveRow(r) && r.feeHfPct === null) {
      missingFeeCount++;
    }
  }
  return { noCoverage, oneDeep, missingFeeCount };
}

/** Distinct case types present in the data, ordered by active-row count desc
 *  then alpha — so the busiest practice areas lead the chip strip and the
 *  order is stable across refetches. A new case type in Airtable appears here
 *  with no code change. */
export function caseTypesPresent(rows: CoverageRow[]): string[] {
  const activeCounts = new Map<string, number>();
  for (const r of rows) {
    if (!activeCounts.has(r.caseType)) activeCounts.set(r.caseType, 0);
    if (isActiveRow(r)) {
      activeCounts.set(r.caseType, (activeCounts.get(r.caseType) ?? 0) + 1);
    }
  }
  return [...activeCounts.keys()].sort((a, b) => {
    const diff = (activeCounts.get(b) ?? 0) - (activeCounts.get(a) ?? 0);
    return diff !== 0 ? diff : a.localeCompare(b);
  });
}

/** Map/strip fill per depth bin. 0 must read as ABSENT (neutral), not as a
 *  faint step of the coverage ramp; 1/2/3+ are alpha steps of the Volume
 *  tab's chart token. No new color tokens (ADR-0101). */
export function fillForDepth(depth: Depth): string {
  if (depth === 0) return "hsl(var(--muted))";
  const alpha = depth === 1 ? 0.3 : depth === 2 ? 0.6 : 1;
  return `hsl(var(--chart-bar) / ${alpha})`;
}

/** "67%" | "—" for the detail card. */
export function formatFee(feeHfPct: number | null): string {
  if (feeHfPct === null) return "—";
  const pct = feeHfPct * 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
}
